// ServiceHistoryPage — hooldusajalugu PCS, BAU, PowerEnv andmetega.
// Lubab valida pargi + ajavahemiku + tehnilisi näitajaid (sõltumatu Ajalugu lehest).

import { useState, useEffect, useMemo } from 'react';
import { Header } from '../components/Layout';
import { useParks } from '../contexts/ParksContext';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer,
} from 'recharts';

const PERIODS = [
  { id: '1h',  label: '1 h',  group: 'rolling' },
  { id: '6h',  label: '6 h',  group: 'rolling' },
  { id: '24h', label: '24 h', group: 'rolling' },
  { id: '7d',  label: '7 päeva',  group: 'rolling' },
  { id: '30d', label: '30 päeva', group: 'rolling' },
  { id: 'today',      label: 'Täna',          group: 'calendar' },
  { id: 'yesterday',  label: 'Eile',          group: 'calendar' },
  { id: 'this_week',  label: 'See nädal',     group: 'calendar' },
  { id: 'this_month', label: 'See kuu',       group: 'calendar' },
  { id: 'this_year',  label: 'See aasta',     group: 'calendar' },
  { id: 'custom',     label: 'Vali ajavahemik', group: 'custom' },
];

// Arvuta from/to ms-timestampid Front-pool, et küsida backendilt eksplitsiit.
function computeRange(period, customFrom, customTo) {
  const now = Date.now();
  if (period === 'custom') {
    if (customFrom && customTo) {
      return { from: new Date(customFrom).getTime(), to: new Date(customTo).getTime() };
    }
    return null;
  }
  if (period === 'today' || period === 'yesterday' || period === 'this_week' || period === 'this_month' || period === 'this_year') {
    const d = new Date();
    const startOfDay = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    if (period === 'today')      return { from: startOfDay, to: now };
    if (period === 'yesterday')  return { from: startOfDay - 86400e3, to: startOfDay - 1 };
    if (period === 'this_week')  { const dow = (d.getDay() + 6) % 7; return { from: startOfDay - dow*86400e3, to: now }; }
    if (period === 'this_month') return { from: new Date(d.getFullYear(), d.getMonth(), 1).getTime(), to: now };
    if (period === 'this_year')  return { from: new Date(d.getFullYear(), 0, 1).getTime(), to: now };
  }
  return null;  // rolling — backend arvutab ise period parameetrist
}

// Grupeeritud näitajad. Igal grupil oma värvi-skeem ja omad y-skaalad.
// Mode-de kaardistus värvideks. Mood renderdatakse värvilise stepped-joonisena (üks joon per mood).
// Iga mood saab oma stable y-positsiooni — nii on ka stepped üleminek lihtne lugeda.
const MODE_VALUES = {
  stop: 0, pause: 1, hold: 2, battery_use: 3, export_prio: 4, charging: 5, max_sell: 6,
};
const MODE_COLORS = {
  stop:        '#374151',  // tume hall
  pause:       '#6b7280',  // hall
  hold:        '#94a3b8',  // helehall
  battery_use: '#22c55e',  // roheline
  export_prio: '#a855f7',  // lilla
  charging:    '#3b82f6',  // sinine
  max_sell:    '#f97316',  // oranž
  failsafe:    '#dc2626',  // punane (kõik failsafe_* variandid)
};
const MODE_LABELS = {
  stop: 'Peata', pause: 'Pausil', hold: 'Hoia ja kogu',
  battery_use: 'Aku kasutus', export_prio: 'Prioriteet ekspordil',
  charging: 'Laadimine', max_sell: 'Maksimaalne müük',
  failsafe: 'Fail-safe',
};
const MODE_KEYS = Object.keys(MODE_VALUES).concat(['failsafe']);
function modeToValue(m) {
  if (!m) return null;
  if (m in MODE_VALUES) return MODE_VALUES[m];
  if (m.startsWith('failsafe_')) return 7;
  return null;
}
// Normaliseeri "failsafe_bau_offline" → "failsafe", muu jätta nagu on
function modeBucket(m) {
  if (!m) return null;
  if (m.startsWith('failsafe_')) return 'failsafe';
  return m;
}
function modeLabel(m) {
  if (!m) return '';
  const b = modeBucket(m);
  return MODE_LABELS[b] || m;
}

const METRIC_GROUPS = {
  'Juhtimine': [
    { id: 'plan_mode',         label: 'Plaani mode',           color: '#11093E', unit: '' },
    { id: 'pcs_setpoint_kw',   label: 'PCS käsk (setpoint)',   color: '#9333ea', unit: 'kW' },
    { id: 'is_panic',          label: 'PANIC override',        color: '#dc2626', unit: '' },
  ],
  'PCS': [
    { id: 'pcs_state',     label: 'PCS olek (kood)', color: '#6d28d9', unit: '' },
    { id: 'pcs_dc_voltage',label: 'PCS DC pinge',    color: '#7c3aed', unit: 'V' },
    { id: 'pcs_dc_current',label: 'PCS DC vool',     color: '#a78bfa', unit: 'A' },
    { id: 'pcs_dc_power',  label: 'PCS DC võimsus',  color: '#c4b5fd', unit: 'kW' },
    { id: 'pcs_ac_power',  label: 'PCS AC võimsus',  color: '#8b5cf6', unit: 'kW' },
    { id: 'pcs_hz',        label: 'PCS sagedus',     color: '#5b21b6', unit: 'Hz' },
    { id: 'pcs_pf',        label: 'PCS PF',          color: '#4c1d95', unit: '' },
    { id: 'pcs_heartbeat', label: 'PCS heartbeat',   color: '#9333ea', unit: '' },
  ],
  'BAU bank': [
    { id: 'bau_voltage',    label: 'BAU pinge',     color: '#15803d', unit: 'V' },
    { id: 'bau_current',    label: 'BAU vool',      color: '#16a34a', unit: 'A' },
    { id: 'bau_soc',        label: 'BAU SOC',       color: '#22c55e', unit: '%' },
    { id: 'bau_soh',        label: 'BAU SOH',       color: '#4ade80', unit: '%' },
    { id: 'bau_insulation', label: 'Isolatsioon',   color: '#65a30d', unit: 'kΩ' },
    { id: 'bau_chg_limit',  label: 'Lub. chg I',    color: '#84cc16', unit: 'A' },
    { id: 'bau_dsg_limit',  label: 'Lub. dsg I',    color: '#a3e635', unit: 'A' },
  ],
  'BAU rakud': [
    { id: 'bau_vcell_max', label: 'Cell max V', color: '#dc2626', unit: 'mV' },
    { id: 'bau_vcell_min', label: 'Cell min V', color: '#f87171', unit: 'mV' },
    { id: 'bau_vcell_avg', label: 'Cell avg V', color: '#fb923c', unit: 'mV' },
    { id: 'bau_tcell_max', label: 'Cell max T', color: '#b45309', unit: '°C' },
    { id: 'bau_tcell_min', label: 'Cell min T', color: '#fbbf24', unit: '°C' },
    { id: 'bau_tcell_avg', label: 'Cell avg T', color: '#f59e0b', unit: '°C' },
  ],
  'Vedelikjahutus': [
    { id: 'lc_unit_state',       label: 'Süsteem',         color: '#1d4ed8', unit: '' },
    { id: 'lc_pump_state',       label: 'Pump',            color: '#2563eb', unit: '' },
    { id: 'lc_compressor_state', label: 'Kompressor',      color: '#3b82f6', unit: '' },
    { id: 'lc_pump_speed',       label: 'Pumba kiirus',    color: '#60a5fa', unit: '%' },
    { id: 'lc_fault_count',      label: 'Faultide arv',    color: '#dc2626', unit: '' },
    { id: 'lc_effluent_temp',    label: 'Vesi väljaminev', color: '#06b6d4', unit: '°C' },
    { id: 'lc_backwater_temp',   label: 'Vesi tagasi',     color: '#0891b2', unit: '°C' },
    { id: 'lc_ambient_temp',     label: 'Ümbritsev temp',  color: '#0284c7', unit: '°C' },
  ],
  'Sisekliima + ohutus': [
    { id: 'env_temperature',  label: 'Sisetemp',  color: '#f59e0b', unit: '°C' },
    { id: 'env_humidity',     label: 'Niiskus',   color: '#0ea5e9', unit: '%RH' },
    { id: 'bms_max_temp_env', label: 'BMS max T', color: '#ea580c', unit: '°C' },
    { id: 'bms_min_temp_env', label: 'BMS min T', color: '#fb923c', unit: '°C' },
    { id: 'smoke_alarm',      label: 'Suitsu alarm', color: '#b91c1c', unit: '' },
    { id: 'dehum_state',      label: 'Niiskuse-eemaldaja', color: '#06b6d4', unit: '' },
    { id: 'heat_state',       label: 'Elektriküte',        color: '#dc2626', unit: '' },
  ],
  // ex9EMS mõõdik (faaside detail) — andmed Telegraf+InfluxDB-st
  'Päikesejaam (mõõdik)': [
    { id: 'pv_voltage_a',     label: 'PV L1 pinge',     color: '#b45309', unit: 'V',   src: 'pv_meter', field: 'voltage_a' },
    { id: 'pv_voltage_b',     label: 'PV L2 pinge',     color: '#d97706', unit: 'V',   src: 'pv_meter', field: 'voltage_b' },
    { id: 'pv_voltage_c',     label: 'PV L3 pinge',     color: '#f59e0b', unit: 'V',   src: 'pv_meter', field: 'voltage_c' },
    { id: 'pv_current_a',     label: 'PV L1 vool',      color: '#15803d', unit: 'A',   src: 'pv_meter', field: 'current_a' },
    { id: 'pv_current_b',     label: 'PV L2 vool',      color: '#22c55e', unit: 'A',   src: 'pv_meter', field: 'current_b' },
    { id: 'pv_current_c',     label: 'PV L3 vool',      color: '#4ade80', unit: 'A',   src: 'pv_meter', field: 'current_c' },
    { id: 'pv_active_power',  label: 'PV aktiivvõimsus',color: '#d97706', unit: 'kW',  src: 'pv_meter', field: 'active_power' },
    { id: 'pv_reactive_power',label: 'PV reaktiivvõimsus', color: '#9a3412', unit: 'kvar', src: 'pv_meter', field: 'reactive_power' },
    { id: 'pv_apparent_power',label: 'PV näiv võimsus', color: '#7c2d12', unit: 'kVA', src: 'pv_meter', field: 'apparent_power' },
    { id: 'pv_power_factor',  label: 'PV PF',           color: '#a16207', unit: '',    src: 'pv_meter', field: 'power_factor' },
    { id: 'pv_frequency',     label: 'PV sagedus',      color: '#854d0e', unit: 'Hz',  src: 'pv_meter', field: 'frequency' },
    { id: 'pv_energy_fwd',    label: 'PV toodetud (E_fwd)',  color: '#15803d', unit: 'kWh', src: 'pv_meter', field: 'energy_import' },
    { id: 'pv_energy_rev',    label: 'PV tagasi (E_rev)',    color: '#b91c1c', unit: 'kWh', src: 'pv_meter', field: 'energy_export' },
    { id: 'pv_energy_total',  label: 'PV kogu (E_total)',    color: '#374151', unit: 'kWh', src: 'pv_meter', field: 'energy_total' },
  ],
  'Inverter (Goodwe)': [
    { id: 'inv_pac',              label: 'Inv Pac',            color: '#d97706', unit: 'W',   src: 'inverter', field: 'pac' },
    { id: 'inv_reactive_power',   label: 'Inv reaktiiv (Q)',   color: '#9a3412', unit: 'var', src: 'inverter', field: 'reactive_power' },
    { id: 'inv_power_factor',     label: 'Inv power factor',   color: '#a16207', unit: '',    src: 'inverter', field: 'power_factor' },
    { id: 'inv_grid_freq',        label: 'Inv grid sagedus',   color: '#92400e', unit: 'Hz',  src: 'inverter', field: 'grid_freq' },
    { id: 'inv_work_mode',        label: 'Inv töörežiim',      color: '#ea580c', unit: '',    src: 'inverter', field: 'work_mode' },
    { id: 'inv_device_status',    label: 'Inv status (Huawei)',color: '#7c3aed', unit: '',    src: 'inverter', field: 'device_status' },
    { id: 'inv_internal_temp',    label: 'Inv sisetemp',       color: '#b45309', unit: '°C',  src: 'inverter', field: 'internal_temp' },
    { id: 'inv_insulation_res',   label: 'Inv isolatsioon',    color: '#65a30d', unit: 'MΩ',  src: 'inverter', field: 'insulation_res' },
    { id: 'inv_efficiency',       label: 'Inv efektiivsus',    color: '#16a34a', unit: '%',   src: 'inverter', field: 'efficiency' },
    { id: 'inv_e_day',            label: 'Inv toodetud täna',  color: '#15803d', unit: 'kWh', src: 'inverter', field: 'e_day' },
    { id: 'inv_e_total',          label: 'Inv toodetud kokku', color: '#166534', unit: 'kWh', src: 'inverter', field: 'e_total' },
    { id: 'inv_warning_code',     label: 'Inv hoiatus',        color: '#dc2626', unit: '',    src: 'inverter', field: 'warning_code' },
    { id: 'inv_function_bits',    label: 'Inv funktsioonibitid',color: '#7c3aed', unit: '',   src: 'inverter', field: 'function_bits' },
    { id: 'inv_pv1_voltage',      label: 'PV1 pinge',          color: '#f59e0b', unit: 'V',   src: 'inverter', field: 'pv1_voltage' },
    { id: 'inv_pv2_voltage',      label: 'PV2 pinge',          color: '#fbbf24', unit: 'V',   src: 'inverter', field: 'pv2_voltage' },
    { id: 'inv_pv3_voltage',      label: 'PV3 pinge',          color: '#fcd34d', unit: 'V',   src: 'inverter', field: 'pv3_voltage' },
    { id: 'inv_pv4_voltage',      label: 'PV4 pinge',          color: '#fde68a', unit: 'V',   src: 'inverter', field: 'pv4_voltage' },
    { id: 'inv_pv1_current',      label: 'PV1 vool',           color: '#16a34a', unit: 'A',   src: 'inverter', field: 'pv1_current' },
    { id: 'inv_pv2_current',      label: 'PV2 vool',           color: '#22c55e', unit: 'A',   src: 'inverter', field: 'pv2_current' },
    { id: 'inv_pv3_current',      label: 'PV3 vool',           color: '#4ade80', unit: 'A',   src: 'inverter', field: 'pv3_current' },
    { id: 'inv_pv4_current',      label: 'PV4 vool',           color: '#86efac', unit: 'A',   src: 'inverter', field: 'pv4_current' },
    { id: 'inv_ac_l1_voltage',    label: 'Inv AC L1 pinge',    color: '#2563eb', unit: 'V',   src: 'inverter', field: 'ac_l1_voltage' },
    { id: 'inv_ac_l2_voltage',    label: 'Inv AC L2 pinge',    color: '#3b82f6', unit: 'V',   src: 'inverter', field: 'ac_l2_voltage' },
    { id: 'inv_ac_l3_voltage',    label: 'Inv AC L3 pinge',    color: '#60a5fa', unit: 'V',   src: 'inverter', field: 'ac_l3_voltage' },
    { id: 'inv_ac_l1_current',    label: 'Inv AC L1 vool',     color: '#9333ea', unit: 'A',   src: 'inverter', field: 'ac_l1_current' },
    { id: 'inv_ac_l2_current',    label: 'Inv AC L2 vool',     color: '#a855f7', unit: 'A',   src: 'inverter', field: 'ac_l2_current' },
    { id: 'inv_ac_l3_current',    label: 'Inv AC L3 vool',     color: '#c084fc', unit: 'A',   src: 'inverter', field: 'ac_l3_current' },
  ],
  'Võrk (mõõdik)': [
    { id: 'grid_voltage_a',     label: 'GRID L1 pinge',     color: '#1d4ed8', unit: 'V',   src: 'grid_meter', field: 'voltage_a' },
    { id: 'grid_voltage_b',     label: 'GRID L2 pinge',     color: '#2563eb', unit: 'V',   src: 'grid_meter', field: 'voltage_b' },
    { id: 'grid_voltage_c',     label: 'GRID L3 pinge',     color: '#3b82f6', unit: 'V',   src: 'grid_meter', field: 'voltage_c' },
    { id: 'grid_current_a',     label: 'GRID L1 vool',      color: '#7c2d12', unit: 'A',   src: 'grid_meter', field: 'current_a' },
    { id: 'grid_current_b',     label: 'GRID L2 vool',      color: '#9a3412', unit: 'A',   src: 'grid_meter', field: 'current_b' },
    { id: 'grid_current_c',     label: 'GRID L3 vool',      color: '#c2410c', unit: 'A',   src: 'grid_meter', field: 'current_c' },
    { id: 'grid_active_power',  label: 'GRID aktiivvõimsus',color: '#2563eb', unit: 'kW',  src: 'grid_meter', field: 'active_power' },
    { id: 'grid_reactive_power',label: 'GRID reaktiivvõimsus', color: '#1e3a8a', unit: 'kvar', src: 'grid_meter', field: 'reactive_power' },
    { id: 'grid_apparent_power',label: 'GRID näiv võimsus', color: '#312e81', unit: 'kVA', src: 'grid_meter', field: 'apparent_power' },
    { id: 'grid_power_factor',  label: 'GRID PF',           color: '#1e40af', unit: '',    src: 'grid_meter', field: 'power_factor' },
    { id: 'grid_frequency',     label: 'GRID sagedus',      color: '#1e3a8a', unit: 'Hz',  src: 'grid_meter', field: 'frequency' },
    { id: 'grid_energy_fwd',    label: 'GRID import (E_fwd)',color: '#b91c1c', unit: 'kWh', src: 'grid_meter', field: 'energy_import' },
    { id: 'grid_energy_rev',    label: 'GRID eksport (E_rev)', color: '#15803d', unit: 'kWh', src: 'grid_meter', field: 'energy_export' },
    { id: 'grid_energy_total',  label: 'GRID kogu (E_total)',  color: '#374151', unit: 'kWh', src: 'grid_meter', field: 'energy_total' },
  ],
  // Deye hübriid (DeyeInverterPoller, /api/parks/:slot/deye-history) — aku + = laadimine, võrk + = import
  'Deye aku': [
    { id: 'deye_bat_soc',     label: 'Aku SOC',          color: '#7c3aed', unit: '%' },
    { id: 'deye_bat_kw',      label: 'Aku võimsus',      color: '#a855f7', unit: 'kW' },
    { id: 'deye_bat_voltage', label: 'Aku pinge',        color: '#6d28d9', unit: 'V' },
    { id: 'deye_bat_temp',    label: 'Aku temperatuur',  color: '#b45309', unit: '°C' },
  ],
  'Deye PV': [
    { id: 'deye_pv_kw',  label: 'PV kokku',  color: '#d97706', unit: 'kW' },
    { id: 'deye_pv1_kw', label: 'PV1',       color: '#f59e0b', unit: 'kW' },
    { id: 'deye_pv2_kw', label: 'PV2',       color: '#fbbf24', unit: 'kW' },
    { id: 'deye_pv1_v',  label: 'PV1 pinge', color: '#b45309', unit: 'V' },
    { id: 'deye_pv2_v',  label: 'PV2 pinge', color: '#92400e', unit: 'V' },
  ],
  'Deye võrk': [
    { id: 'deye_grid_kw',   label: 'Võrk (+ import)', color: '#2563eb', unit: 'kW' },
    { id: 'deye_grid_v_l1', label: 'L1 pinge',        color: '#1d4ed8', unit: 'V' },
    { id: 'deye_grid_v_l2', label: 'L2 pinge',        color: '#3b82f6', unit: 'V' },
    { id: 'deye_grid_v_l3', label: 'L3 pinge',        color: '#60a5fa', unit: 'V' },
    { id: 'deye_grid_hz',   label: 'Sagedus',         color: '#1e3a8a', unit: 'Hz' },
  ],
  'Deye koormus + inverter': [
    { id: 'deye_load_kw', label: 'Koormus',          color: '#16a34a', unit: 'kW' },
    { id: 'deye_out_kw',  label: 'Inverteri väljund', color: '#15803d', unit: 'kW' },
    { id: 'deye_temp_dc', label: 'DC temperatuur',   color: '#dc2626', unit: '°C' },
    { id: 'deye_temp_ac', label: 'AC temperatuur',   color: '#f97316', unit: '°C' },
  ],
};

// Deye rühmad kuvatakse ainult Deye parkide valimisel (ja peidetakse ainult tava-parkide puhul)
const DEYE_GROUPS = new Set(['Deye aku', 'Deye PV', 'Deye võrk', 'Deye koormus + inverter']);
const DEYE_DEFAULT_METRICS = ['deye_bat_soc', 'deye_bat_kw', 'deye_pv_kw', 'deye_grid_kw', 'deye_load_kw'];

// Lapsendamine flat'iks
const ALL_METRICS = Object.entries(METRIC_GROUPS).flatMap(([group, items]) =>
  items.map(m => ({ ...m, group }))
);

function fmtTime(ts, period) {
  const d = new Date(ts);
  if (period === '1h' || period === '6h' || period === '24h') {
    return d.toLocaleTimeString('et-EE', { hour: '2-digit', minute: '2-digit' });
  }
  if (period === '7d' || period === '30d') {
    return d.toLocaleDateString('et-EE', { month: 'short', day: 'numeric' }) + ' ' +
           d.toLocaleTimeString('et-EE', { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString('et-EE', { month: 'short', day: 'numeric' });
}

function fmt(v, decimals = 2) {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  return v.toLocaleString('et-EE', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

export function ServiceHistoryPage({ wsConnected }) {
  const { data: parksData } = useParks();
  const onlineParks = useMemo(() =>
    (parksData?.parks || []).filter(p => p.name).map(p => ({
      slot: p.slot, name: p.name, online: p.online, deye: p.meter_protocol === 'deye_v5',
    })), [parksData]);

  const [selectedSlots, setSelectedSlots] = useState([]);  // mitu parki korraga
  const [period, setPeriod] = useState('24h');
  // Custom-range — kasutaja sisestab need date-input'idesse (YYYY-MM-DDTHH:MM)
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  // Vaikimisi tühi — kasutaja peab ise vajutama, mida näha tahab
  const [selectedMetrics, setSelectedMetrics] = useState([]);
  // Per-pargi andmed: { slot: { snapshots: [], meterPv: [], meterGrid: [], inverter: [] } }
  const [parkData, setParkData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [updated, setUpdated] = useState(null);

  // Vaikimisi vali esimene online park
  useEffect(() => {
    if (onlineParks.length === 0) return;
    const valid = selectedSlots.filter(s => onlineParks.some(p => p.slot === s));
    if (valid.length === 0) {
      const firstOnline = onlineParks.find(p => p.online) || onlineParks[0];
      if (firstOnline) setSelectedSlots([firstOnline.slot]);
    } else if (valid.length !== selectedSlots.length) {
      setSelectedSlots(valid);
    }
  }, [onlineParks, selectedSlots]);

  // Valitud parkide tüüp → milliseid näitajate rühmi näidata
  const selDeye = selectedSlots.filter(s => onlineParks.find(p => p.slot === s)?.deye).length;
  const onlyDeye = selectedSlots.length > 0 && selDeye === selectedSlots.length;
  const visibleGroups = Object.entries(METRIC_GROUPS).filter(([g]) =>
    DEYE_GROUPS.has(g) ? selDeye > 0 : !onlyDeye);
  // Ainult Deye pargid valitud ja näitajaid pole → vali olulisemad vaikimisi
  useEffect(() => {
    if (onlyDeye && selectedMetrics.length === 0) setSelectedMetrics(DEYE_DEFAULT_METRICS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlyDeye]);

  const toggleSlot = (slot) => {
    setSelectedSlots(prev => prev.includes(slot) ? prev.filter(s => s !== slot) : [...prev, slot].sort((a, b) => a - b));
  };

  useEffect(() => {
    let cancel = false;
    async function load() {
      if (selectedSlots.length === 0) {
        setParkData({});
        return;
      }
      setLoading(true);
      setError(null);
      // Arvuta from/to kohalik aeg, et siis backend ei lähe segadusse
      const range = computeRange(period, customFrom, customTo);
      const qs = range
        ? `from=${range.from}&to=${range.to}`
        : `period=${period}`;
      try {
        const result = {};
        // Plan-history ja execution-log võtavad ms-timestamp parameetrid (from/to)
        const planRangeMs = range
          ? range
          : (function () {
              // Rolling period → arvuta ms
              const now = Date.now();
              const map = { '1h': 3600e3, '6h': 6*3600e3, '24h': 86400e3, '7d': 7*86400e3, '30d': 30*86400e3 };
              return { from: now - (map[period] || 86400e3), to: now };
            })();
        const planQs = `from=${planRangeMs.from}&to=${planRangeMs.to}`;
        // Fetch parallel kõik valitud pargid + nende kõik allikad
        await Promise.all(selectedSlots.map(async (slot) => {
          if (onlineParks.find(p => p.slot === slot)?.deye) {
            const dRes = await fetch(`/api/parks/${slot}/deye-history?${qs}`);
            const dJson = dRes.ok ? await dRes.json() : { points: [] };
            result[slot] = { deye: dJson.points || [], snapshots: [], meterPv: [], meterGrid: [],
                             inverter: [], planHistory: [], planExec: [] };
            return;
          }
          const [sRes, pvRes, gridRes, invRes, planRes, execRes] = await Promise.all([
            fetch(`/api/parks/${slot}/service-history?${qs}`),
            fetch(`/api/parks/${slot}/meter-history?${qs}&device=pv_meter`),
            fetch(`/api/parks/${slot}/meter-history?${qs}&device=grid_meter`),
            fetch(`/api/parks/${slot}/inverter-history?${qs}`),
            fetch(`/api/sites/${slot}/plan-history?${planQs}`),
            fetch(`/api/sites/${slot}/plan-execution-log?${planQs}`),
          ]);
          const sJson = sRes.ok ? await sRes.json() : { snapshots: [] };
          const pvJson = pvRes.ok ? await pvRes.json() : { points: [] };
          const gridJson = gridRes.ok ? await gridRes.json() : { points: [] };
          const invJson = invRes.ok ? await invRes.json() : { points: [] };
          const planJson = planRes.ok ? await planRes.json() : { points: [] };
          const execJson = execRes.ok ? await execRes.json() : { points: [] };
          result[slot] = {
            snapshots: sJson.snapshots || [],
            meterPv: pvJson.points || [],
            meterGrid: gridJson.points || [],
            inverter: invJson.points || [],
            planHistory: planJson.points || [],
            planExec: execJson.points || [],
          };
        }));
        if (cancel) return;
        setParkData(result);
        setUpdated(Date.now());
      } catch (err) {
        if (!cancel) setError(err.message);
      } finally {
        if (!cancel) setLoading(false);
      }
    }
    load();
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSlots, period, customFrom, customTo, onlineParks.length]);

  // Ehita ühine graafikuandmestik — kõik valitud pargid sama timeline'i peal.
  // Iga väli saab pargi-suffiksi: pcs_state_p1, pcs_state_p3 jne.
  const chartData = useMemo(() => {
    const byTs = new Map();
    const upsert = (ts, fields) => {
      if (byTs.has(ts)) Object.assign(byTs.get(ts), fields);
      else byTs.set(ts, { ts, time: fmtTime(ts, period), ...fields });
    };
    for (const slot of selectedSlots) {
      const pd = parkData[slot];
      if (!pd) continue;
      const suffix = `_p${slot}`;
      // Service snapshot — kõik fieldid saavad suffiks-i
      for (const s of pd.snapshots) {
        const fields = {};
        for (const [k, v] of Object.entries(s)) {
          if (k === 'ts' || k === 'slot') continue;
          fields[`${k}${suffix}`] = v;
        }
        upsert(s.ts, fields);
      }
      // PV mõõdik
      for (const p of pd.meterPv) {
        upsert(p.ts, {
          [`pv_voltage_a${suffix}`]: p.voltage_a, [`pv_voltage_b${suffix}`]: p.voltage_b, [`pv_voltage_c${suffix}`]: p.voltage_c,
          [`pv_current_a${suffix}`]: p.current_a, [`pv_current_b${suffix}`]: p.current_b, [`pv_current_c${suffix}`]: p.current_c,
          [`pv_active_power${suffix}`]: p.active_power, [`pv_reactive_power${suffix}`]: p.reactive_power, [`pv_apparent_power${suffix}`]: p.apparent_power,
          [`pv_power_factor${suffix}`]: p.power_factor, [`pv_frequency${suffix}`]: p.frequency,
          [`pv_energy_fwd${suffix}`]: p.energy_import, [`pv_energy_rev${suffix}`]: p.energy_export, [`pv_energy_total${suffix}`]: p.energy_total,
        });
      }
      // GRID mõõdik
      for (const p of pd.meterGrid) {
        upsert(p.ts, {
          [`grid_voltage_a${suffix}`]: p.voltage_a, [`grid_voltage_b${suffix}`]: p.voltage_b, [`grid_voltage_c${suffix}`]: p.voltage_c,
          [`grid_current_a${suffix}`]: p.current_a, [`grid_current_b${suffix}`]: p.current_b, [`grid_current_c${suffix}`]: p.current_c,
          [`grid_active_power${suffix}`]: p.active_power, [`grid_reactive_power${suffix}`]: p.reactive_power, [`grid_apparent_power${suffix}`]: p.apparent_power,
          [`grid_power_factor${suffix}`]: p.power_factor, [`grid_frequency${suffix}`]: p.frequency,
          [`grid_energy_fwd${suffix}`]: p.energy_import, [`grid_energy_rev${suffix}`]: p.energy_export, [`grid_energy_total${suffix}`]: p.energy_total,
        });
      }
      // Inverter
      for (const p of pd.inverter) {
        const f = {};
        for (const [k, v] of Object.entries(p)) {
          if (k === 'ts') continue;
          f[`inv_${k}${suffix}`] = v;
        }
        upsert(p.ts, f);
      }
      // Deye hübriid — iga väli saab deye_ eesliite (vt METRIC_GROUPS 'Deye ...')
      for (const p of pd.deye || []) {
        const f = {};
        for (const [k, v] of Object.entries(p)) {
          if (k === 'ts') continue;
          f[`deye_${k}${suffix}`] = v;
        }
        upsert(p.ts, f);
      }
      // Plaani moodide ajalugu — 15-min sammuga. Igale moodile oma dataKey, et saaks värvilist Line'i.
      for (const p of pd.planHistory || []) {
        const bucket = modeBucket(p.mode);
        const fields = {
          [`plan_mode${suffix}`]: modeToValue(p.mode),
          [`plan_mode_name${suffix}`]: p.mode,
        };
        // Iga mood saab oma stable y-positsiooni (= MODE_VALUES[bucket]) ainult kui see mood on aktiivne
        for (const k of MODE_KEYS) {
          fields[`plan_${k}${suffix}`] = (bucket === k) ? (MODE_VALUES[k] ?? 7) : null;
        }
        upsert(p.ts, fields);
      }
      // Plaani tegelik täideviimine (per-sekundi setPower käsud)
      for (const p of pd.planExec || []) {
        upsert(p.ts, {
          [`pcs_setpoint_kw${suffix}`]: p.pcs_setpoint_kw,
          [`is_panic${suffix}`]: p.is_panic ? 1 : 0,
        });
      }
    }
    return [...byTs.values()].sort((a, b) => a.ts - b.ts);
  }, [parkData, selectedSlots, period]);

  // Kogu snapshot-loend kõikidest valitud parkidest (alarm log jaoks — kuvab esimese pargi oma)
  const snapshots = useMemo(() => {
    const firstSlot = selectedSlots[0];
    return parkData[firstSlot]?.snapshots || [];
  }, [parkData, selectedSlots]);

  const visibleMetrics = ALL_METRICS.filter(m => selectedMetrics.includes(m.id));
  const toggleMetric = (id) =>
    setSelectedMetrics(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  // Min/max/keskmine valitud perioodi kohta iga (metrika × park) jaoks.
  // Kuvab tabelina chart-i kohal — kasutaja näeb hooldus-ajaloos kohe vahemiku.
  const chartStats = useMemo(() => {
    const stats = {};  // key `${metricId}_p${slot}` → { min, max, avg, last, n }
    for (const m of visibleMetrics) {
      for (const slot of selectedSlots) {
        const key = `${m.id}_p${slot}`;
        let mn = Infinity, mx = -Infinity, sum = 0, n = 0, last = null;
        for (const row of chartData) {
          const v = row[key];
          if (typeof v !== 'number' || !Number.isFinite(v)) continue;
          if (v < mn) mn = v;
          if (v > mx) mx = v;
          sum += v; n++; last = v;
        }
        if (n > 0) stats[key] = { min: mn, max: mx, avg: sum / n, last, n };
      }
    }
    return stats;
  }, [chartData, visibleMetrics, selectedSlots]);

  // selectedPark eemaldatud — kasutame nüüd selectedSlots arrayd

  // Alarm log — viimased snapshotid kus alarm bitt aktiivne
  const alarmLog = useMemo(() => {
    const events = [];
    let prev = { fault: 0, l1: 0, l2: 0, l3: 0, smoke: 0, lc_fault: 0 };
    for (const s of snapshots) {
      const cur = {
        fault: s.bau_fault_state || 0,
        l1: s.bau_alarm_l1 || 0,
        l2: s.bau_alarm_l2 || 0,
        l3: s.bau_alarm_l3 || 0,
        smoke: s.smoke_alarm || 0,
        lc_fault: s.lc_fault_count || 0,
      };
      // Tuvasta muutused
      if (cur.fault !== prev.fault || cur.l3 !== prev.l3 || cur.smoke !== prev.smoke) {
        events.push({
          ts: s.ts,
          fault_state: cur.fault,
          alarm_l1: cur.l1, alarm_l2: cur.l2, alarm_l3: cur.l3,
          smoke: cur.smoke, lc_fault: cur.lc_fault,
        });
      }
      prev = cur;
    }
    return events.slice(-20).reverse();  // viimased 20 muutust
  }, [snapshots]);

  return (
    <div className="h-full flex flex-col" style={{ background: 'var(--bg)' }}>
      <Header title="Hooldusajalugu" connected={wsConnected} lastUpdate={updated} />

      <div style={{ flex: 1, overflow: 'auto', padding: '20px 28px 40px', maxWidth: 1480, width: '100%', margin: '0 auto' }}>
        {/* Filtrid */}
        <div style={{
          display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 20,
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 6, padding: '14px 16px',
        }}>
          <div style={{ flex: '1 1 100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <div style={{ fontSize: '0.62rem', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Pargid · klõpsa valimiseks (mitut korraga = võrdlus)
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                <button
                  onClick={() => setSelectedSlots(onlineParks.map(p => p.slot))}
                  style={{ padding: '3px 8px', borderRadius: 3, border: '1px solid var(--border)', background: 'white', fontSize: '0.66rem', cursor: 'pointer' }}
                >
                  Kõik
                </button>
                <button
                  onClick={() => setSelectedSlots([])}
                  style={{ padding: '3px 8px', borderRadius: 3, border: '1px solid var(--border)', background: 'white', fontSize: '0.66rem', cursor: 'pointer' }}
                >
                  Tühi
                </button>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {onlineParks.map(p => {
                const active = selectedSlots.includes(p.slot);
                return (
                  <button
                    key={p.slot}
                    onClick={() => toggleSlot(p.slot)}
                    style={{
                      padding: '4px 10px', borderRadius: 3,
                      border: `1px solid ${active ? '#18181b' : 'var(--border)'}`,
                      background: active ? '#18181b' : 'white',
                      color: active ? 'white' : (p.online ? 'var(--text-2)' : 'var(--text-3)'),
                      fontFamily: 'inherit', fontSize: '0.7rem', fontWeight: 500,
                      cursor: 'pointer',
                      opacity: p.online ? 1 : 0.6,
                    }}
                  >
                    #{p.slot} {p.name}
                    {!p.online && <span style={{ marginLeft: 4, opacity: 0.6 }}>·off</span>}
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ flex: '1 1 100%' }}>
            <div style={{ fontSize: '0.62rem', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
              Periood
            </div>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
              {/* Rolling */}
              {PERIODS.filter(p => p.group === 'rolling').map(p => (
                <button
                  key={p.id}
                  onClick={() => setPeriod(p.id)}
                  style={{
                    padding: '5px 11px', borderRadius: 4,
                    border: period === p.id ? '1px solid #18181b' : '1px solid var(--border)',
                    background: period === p.id ? '#18181b' : 'white',
                    color: period === p.id ? 'white' : 'var(--text-2)',
                    fontFamily: 'inherit', fontSize: '0.7rem', fontWeight: 500,
                    cursor: 'pointer',
                  }}
                >
                  {p.label}
                </button>
              ))}
              <span style={{ width: 1, height: 18, background: 'var(--border)', margin: '0 4px' }} />
              {/* Calendar */}
              {PERIODS.filter(p => p.group === 'calendar').map(p => (
                <button
                  key={p.id}
                  onClick={() => setPeriod(p.id)}
                  style={{
                    padding: '5px 11px', borderRadius: 4,
                    border: period === p.id ? '1px solid #18181b' : '1px solid var(--border)',
                    background: period === p.id ? '#18181b' : 'white',
                    color: period === p.id ? 'white' : 'var(--text-2)',
                    fontFamily: 'inherit', fontSize: '0.7rem', fontWeight: 500,
                    cursor: 'pointer',
                  }}
                >
                  {p.label}
                </button>
              ))}
              <span style={{ width: 1, height: 18, background: 'var(--border)', margin: '0 4px' }} />
              {/* Custom */}
              <button
                onClick={() => setPeriod('custom')}
                style={{
                  padding: '5px 11px', borderRadius: 4,
                  border: period === 'custom' ? '1px solid #18181b' : '1px solid var(--border)',
                  background: period === 'custom' ? '#18181b' : 'white',
                  color: period === 'custom' ? 'white' : 'var(--text-2)',
                  fontFamily: 'inherit', fontSize: '0.7rem', fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                📅 Vali ajavahemik
              </button>
            </div>
            {/* Custom range date-pickers — nähtav ainult kui period == 'custom' */}
            {period === 'custom' && (
              <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span style={{ fontSize: '0.6rem', color: 'var(--text-3)' }}>Algus</span>
                  <input
                    type="datetime-local"
                    value={customFrom}
                    onChange={e => setCustomFrom(e.target.value)}
                    style={{
                      padding: '5px 8px', borderRadius: 4,
                      border: '1px solid var(--border-strong)', background: 'white',
                      fontFamily: 'inherit', fontSize: '0.75rem',
                    }}
                  />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span style={{ fontSize: '0.6rem', color: 'var(--text-3)' }}>Lõpp</span>
                  <input
                    type="datetime-local"
                    value={customTo}
                    onChange={e => setCustomTo(e.target.value)}
                    style={{
                      padding: '5px 8px', borderRadius: 4,
                      border: '1px solid var(--border-strong)', background: 'white',
                      fontFamily: 'inherit', fontSize: '0.75rem',
                    }}
                  />
                </label>
                <div style={{ display: 'flex', gap: 4, marginTop: 18 }}>
                  <button
                    onClick={() => {
                      // Kiir-eelseaded
                      const now = new Date();
                      const toStr = now.toISOString().slice(0, 16);
                      const fromD = new Date(now.getTime() - 7*86400e3);
                      setCustomFrom(fromD.toISOString().slice(0, 16));
                      setCustomTo(toStr);
                    }}
                    style={{ padding: '4px 8px', fontSize: '0.66rem', border: '1px solid var(--border)', borderRadius: 3, background: 'white', cursor: 'pointer' }}
                  >
                    Viimane 7 päeva
                  </button>
                  <button
                    onClick={() => { setCustomFrom(''); setCustomTo(''); }}
                    style={{ padding: '4px 8px', fontSize: '0.66rem', border: '1px solid var(--border)', borderRadius: 3, background: 'white', cursor: 'pointer' }}
                  >
                    Tühjenda
                  </button>
                </div>
                {(!customFrom || !customTo) && (
                  <span style={{ fontSize: '0.7rem', color: '#dc2626', marginTop: 18 }}>
                    ⚠ Vali nii algus kui lõpp
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Näitajad grupeeritult */}
        <div style={{
          marginBottom: 20, background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 6, padding: '12px 16px',
        }}>
          <div style={{ fontSize: '0.62rem', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
            Näitajad — vali kategooriaid
          </div>
          {visibleGroups.map(([groupName, items]) => (
            <div key={groupName} style={{ marginBottom: 8 }}>
              <div style={{ fontSize: '0.62rem', color: 'var(--text-2)', fontWeight: 600, marginBottom: 4 }}>
                {groupName}
              </div>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {items.map(m => {
                  const active = selectedMetrics.includes(m.id);
                  return (
                    <button
                      key={m.id}
                      onClick={() => toggleMetric(m.id)}
                      style={{
                        padding: '3px 9px', borderRadius: 3,
                        border: `1px solid ${active ? m.color : 'var(--border)'}`,
                        background: active ? m.color + '20' : 'white',
                        color: active ? m.color : 'var(--text-2)',
                        fontFamily: 'inherit', fontSize: '0.66rem', fontWeight: 500,
                        cursor: 'pointer',
                      }}
                    >
                      {m.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Status */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, fontSize: '0.7rem', color: 'var(--text-3)' }}>
          <div>
            {selectedSlots.length > 0 ? (
              <span><strong style={{ color: 'var(--text)' }}>{selectedSlots.length} parki valitud</strong> · {chartData.length} ajapunkti</span>
            ) : (
              <span>Pole parki valitud</span>
            )}
          </div>
          {loading && <span>Laadin...</span>}
          {error && <span style={{ color: 'var(--negative)' }}>Viga: {error}</span>}
        </div>

        {/* Graafik */}
        {chartData.length === 0 && !loading && !error && selectedSlots.length > 0 && (
          <div style={{
            padding: 40, textAlign: 'center', color: 'var(--text-3)',
            background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6,
          }}>
            Hooldusajaloo andmed selle perioodi kohta puuduvad.<br/>
            <span style={{ fontSize: '0.7rem' }}>Service snapshotid salvestatakse iga 5 minuti tagant; meter/inverter ajalugu InfluxDB-s.</span>
          </div>
        )}

        {/* Plaani moodide värviline timeline-riba (kuvatakse kui "Plaani mode" on valitud).
            Joondub allpool oleva chart-i plot-alaga: chart YAxis width=70 + margin.left=10 = 80px vasakult.
            Chart container ja timeline container mõlemad padding: 16. Bar nihke kalkuleerimisel:
              chart sees plot algab 16 (padding) + 80 (axis) = 96 outer left edge
              timeline-i bar peab algama samast — kasutame chart-i siseses paigutusega:
                paddingLeft: 80 ja paddingRight: 30 (= chart-i margin.right) */}
        {selectedMetrics.includes('plan_mode') && selectedSlots.length > 0 && (
          <div style={{
            background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6,
            padding: 16, marginBottom: 8,
          }}>
            <div style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
              Plaani moodide ajalugu
            </div>
            {selectedSlots.map(slot => {
              const planPoints = parkData[slot]?.planHistory || [];
              const parkName = onlineParks.find(p => p.slot === slot)?.name || `slot ${slot}`;
              const labelStyle = {
                position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)',
                width: 76, fontSize: 11, color: 'var(--text-2)', fontWeight: 500,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              };
              const wrapStyle = {
                position: 'relative', paddingLeft: 80, paddingRight: 30,
                marginBottom: 4, minHeight: 20, boxSizing: 'border-box',
              };
              if (!planPoints.length) {
                return (
                  <div key={slot} style={wrapStyle}>
                    <span style={labelStyle}>{parkName}</span>
                    <div style={{ height: 18, fontSize: 11, color: 'var(--text-3)', fontStyle: 'italic' }}>—</div>
                  </div>
                );
              }
              const tFirst = planPoints[0].ts;
              const tLast  = planPoints[planPoints.length - 1].ts + 15 * 60_000;
              const totalMs = tLast - tFirst;
              const segments = planPoints.map((p, i) => {
                const start = p.ts;
                const end = i + 1 < planPoints.length ? planPoints[i + 1].ts : tLast;
                return { mode: p.mode, start, end, widthPct: ((end - start) / totalMs) * 100 };
              });
              return (
                <div key={slot} style={wrapStyle}>
                  <span style={labelStyle}>{parkName}</span>
                  <div style={{ display: 'flex', height: 18, borderRadius: 2, overflow: 'hidden', border: '1px solid var(--border)' }}>
                    {segments.map((s, i) => (
                      <div key={i}
                        title={`${new Date(s.start).toLocaleString('et-EE', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })} — ${modeLabel(s.mode)}`}
                        style={{
                          width: `${s.widthPct}%`,
                          background: MODE_COLORS[modeBucket(s.mode)] || '#ccc',
                        }} />
                    ))}
                  </div>
                </div>
              );
            })}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 8, fontSize: 11, color: 'var(--text-3)' }}>
              {MODE_KEYS.map(k => (
                <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 2, background: MODE_COLORS[k] }} />
                  {MODE_LABELS[k] || k}
                </div>
              ))}
            </div>
          </div>
        )}

        {chartData.length > 0 && visibleMetrics.length > 0 && Object.keys(chartStats).length > 0 && (
          <div style={{
            background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6,
            padding: '10px 14px', marginBottom: 12, overflowX: 'auto',
          }}>
            <div style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-3)', fontWeight: 500, marginBottom: 6 }}>
              Statistika valitud perioodi kohta (min · max · keskmine)
            </div>
            <table style={{ width: '100%', fontSize: '0.78rem', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ color: 'var(--text-2)', textAlign: 'left' }}>
                  <th style={{ padding: '4px 8px', borderBottom: '1px solid var(--border)', fontWeight: 500 }}>Metrika</th>
                  {selectedSlots.length > 1 && (
                    <th style={{ padding: '4px 8px', borderBottom: '1px solid var(--border)', fontWeight: 500 }}>Park</th>
                  )}
                  <th style={{ padding: '4px 8px', borderBottom: '1px solid var(--border)', fontWeight: 500, textAlign: 'right' }}>Min</th>
                  <th style={{ padding: '4px 8px', borderBottom: '1px solid var(--border)', fontWeight: 500, textAlign: 'right' }}>Max</th>
                  <th style={{ padding: '4px 8px', borderBottom: '1px solid var(--border)', fontWeight: 500, textAlign: 'right' }}>Keskmine</th>
                  <th style={{ padding: '4px 8px', borderBottom: '1px solid var(--border)', fontWeight: 500, textAlign: 'right' }}>Viimane</th>
                  <th style={{ padding: '4px 8px', borderBottom: '1px solid var(--border)', fontWeight: 500, textAlign: 'right' }}>Mõõtmisi</th>
                </tr>
              </thead>
              <tbody>
                {visibleMetrics.flatMap(m =>
                  selectedSlots.map(slot => {
                    const st = chartStats[`${m.id}_p${slot}`];
                    if (!st) return null;
                    const parkName = onlineParks.find(p => p.slot === slot)?.name || `slot ${slot}`;
                    const dec = m.unit === 'kW' || m.unit === 'V' || m.unit === 'kvar' || m.unit === 'kVA' ? 2 : 1;
                    return (
                      <tr key={`stat_${m.id}_p${slot}`} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '4px 8px' }}>
                          <span style={{ display: 'inline-block', width: 8, height: 8, background: m.color, borderRadius: 2, marginRight: 6, verticalAlign: 'middle' }} />
                          {m.label}
                        </td>
                        {selectedSlots.length > 1 && (
                          <td style={{ padding: '4px 8px', color: 'var(--text-2)' }}>{parkName}</td>
                        )}
                        <td style={{ padding: '4px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(st.min, dec)} {m.unit}</td>
                        <td style={{ padding: '4px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(st.max, dec)} {m.unit}</td>
                        <td style={{ padding: '4px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--text-2)' }}>{fmt(st.avg, dec)} {m.unit}</td>
                        <td style={{ padding: '4px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--text-2)' }}>{fmt(st.last, dec)} {m.unit}</td>
                        <td style={{ padding: '4px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--text-3)' }}>{st.n}</td>
                      </tr>
                    );
                  }).filter(Boolean)
                )}
              </tbody>
            </table>
          </div>
        )}

        {chartData.length > 0 && visibleMetrics.length > 0 && (
          <div style={{
            background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6,
            padding: 16, height: 480, marginBottom: 20,
          }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f1f4" />
                <XAxis dataKey="time" stroke="#71717a" fontSize={11} />
                <YAxis
                  stroke="#71717a" fontSize={11}
                  domain={[
                    (dataMin) => {
                      const m = Number.isFinite(dataMin) ? dataMin : 0;
                      return m - Math.max(0.05 * Math.abs(m || 1), 1);
                    },
                    (dataMax) => {
                      const m = Number.isFinite(dataMax) ? dataMax : 1;
                      return m + Math.max(0.05 * Math.abs(m || 1), 1);
                    },
                  ]}
                  allowDecimals
                  width={70}
                  tickFormatter={(v) => {
                    if (v == null) return '';
                    const abs = Math.abs(v);
                    if (abs >= 1e9) return (v / 1e9).toFixed(2) + ' G';
                    if (abs >= 1e6) return (v / 1e6).toFixed(2) + ' M';
                    if (abs >= 1e3) return (v / 1e3).toFixed(1) + ' k';
                    return v.toLocaleString('et-EE', { maximumFractionDigits: 2 });
                  }}
                />
                <Tooltip
                  contentStyle={{ background: 'white', border: '1px solid var(--border-strong)', borderRadius: 4, fontSize: '0.75rem' }}
                  labelFormatter={(label) => `Aeg: ${label}`}
                  formatter={(value, name) => {
                    // Leia metrika algse label/unit järgi name-st
                    const m = ALL_METRICS.find(x => name.startsWith(x.label));
                    return [value !== null ? `${fmt(value, 2)} ${m?.unit || ''}` : '—', name];
                  }}
                />
                <Legend wrapperStyle={{ fontSize: '0.72rem' }} />
                {visibleMetrics.flatMap((m, mIdx) =>
                  selectedSlots.map((slot, sIdx) => {
                    // Iga park saab unikaalse värvi (mitte hue-rotate filter)
                    const SLOT_COLORS = [
                      '#003E51', '#16a34a', '#dc2626', '#d97706', '#7c3aed',
                      '#0891b2', '#be185d', '#0d9488', '#a16207', '#1d4ed8',
                      '#9333ea', '#0e7490', '#65a30d', '#c2410c', '#4338ca',
                    ];
                    const DASH = [null, '6 3', '2 3', '8 3 2 3', '12 3'];
                    const parkColor = selectedSlots.length === 1 ? m.color : SLOT_COLORS[sIdx % SLOT_COLORS.length];
                    const dashed = visibleMetrics.length === 1 ? null : DASH[mIdx % DASH.length];
                    const parkName = onlineParks.find(p => p.slot === slot)?.name || `slot ${slot}`;
                    // Stepped (kategooriline) muutuv mode/status — astmeline joonis on intuitiivsem
                    const stepped = ['plan_mode', 'is_panic', 'pcs_state', 'inv_work_mode', 'lc_unit_state'].includes(m.id);
                    // Plaani mode: renderdame ühe Line'i AINULT pargi-värviga (referents) +
                    // lisaks ÜHE Line'i per mood, värvitud vastavalt MODE_COLORS-le.
                    // plan_mode kuvame eraldi värvilise timeline-ribana ülal pool chart-i (vt allpool)
                    // — siin chart-is rida ei renderda
                    if (m.id === 'plan_mode') return null;
                    return (
                      <Line
                        key={`${m.id}_p${slot}`}
                        type={stepped ? 'stepAfter' : 'monotone'}
                        dataKey={`${m.id}_p${slot}`}
                        name={selectedSlots.length === 1 ? m.label : `${m.label} · ${parkName}`}
                        stroke={parkColor}
                        strokeWidth={stepped ? 2.4 : 1.8}
                        strokeDasharray={dashed}
                        dot={false}
                        isAnimationActive={false}
                        connectNulls
                      />
                    );
                  })
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Alarm log */}
        {alarmLog.length > 0 && (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden', marginBottom: 20 }}>
            <div style={{ padding: '10px 16px', background: 'var(--surface-2)', borderBottom: '1px solid var(--border)', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-3)', fontWeight: 500 }}>
              Alarm log · BAU fault/alarm bittide muutused
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.7rem', fontFamily: 'JetBrains Mono, monospace' }}>
              <thead style={{ background: 'var(--surface-2)' }}>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  <th style={{ padding: '6px 12px', textAlign: 'left', color: 'var(--text-3)' }}>Aeg</th>
                  <th style={{ padding: '6px 12px', textAlign: 'right', color: 'var(--text-3)' }}>Fault</th>
                  <th style={{ padding: '6px 12px', textAlign: 'right', color: 'var(--text-3)' }}>L1</th>
                  <th style={{ padding: '6px 12px', textAlign: 'right', color: 'var(--text-3)' }}>L2</th>
                  <th style={{ padding: '6px 12px', textAlign: 'right', color: 'var(--text-3)' }}>L3</th>
                  <th style={{ padding: '6px 12px', textAlign: 'right', color: 'var(--text-3)' }}>Suits</th>
                  <th style={{ padding: '6px 12px', textAlign: 'right', color: 'var(--text-3)' }}>LC viga</th>
                </tr>
              </thead>
              <tbody>
                {alarmLog.map((e, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--divider)' }}>
                    <td style={{ padding: '4px 12px', color: 'var(--text-2)' }}>
                      {new Date(e.ts).toLocaleString('et-EE', { hour12: false })}
                    </td>
                    <td style={{ padding: '4px 12px', textAlign: 'right', color: e.fault_state ? 'var(--negative)' : 'var(--text-3)' }}>
                      0x{(e.fault_state || 0).toString(16).padStart(4, '0').toUpperCase()}
                    </td>
                    <td style={{ padding: '4px 12px', textAlign: 'right', color: e.alarm_l1 ? 'var(--info)' : 'var(--text-3)' }}>
                      0x{(e.alarm_l1 || 0).toString(16).padStart(4, '0').toUpperCase()}
                    </td>
                    <td style={{ padding: '4px 12px', textAlign: 'right', color: e.alarm_l2 ? 'var(--warning)' : 'var(--text-3)' }}>
                      0x{(e.alarm_l2 || 0).toString(16).padStart(4, '0').toUpperCase()}
                    </td>
                    <td style={{ padding: '4px 12px', textAlign: 'right', color: e.alarm_l3 ? 'var(--negative)' : 'var(--text-3)' }}>
                      0x{(e.alarm_l3 || 0).toString(16).padStart(4, '0').toUpperCase()}
                    </td>
                    <td style={{ padding: '4px 12px', textAlign: 'right', color: e.smoke ? 'var(--negative)' : 'var(--text-3)' }}>
                      {e.smoke || 0}
                    </td>
                    <td style={{ padding: '4px 12px', textAlign: 'right', color: e.lc_fault ? 'var(--warning)' : 'var(--text-3)' }}>
                      {e.lc_fault || 0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default ServiceHistoryPage;
